// src/components/FileManager/FileRibbon.jsx
import React from "react";
import {
  Lock,
  PlusCircle,
  BookOpen,
  Save,
  Trash2,
  Copy,
  FolderOpen,
  LoaderCircle,
} from "lucide-react";

/**
 * FileRibbon
 * --------------------------------------------------------------------
 * Generic file ribbon UI.
 *
 * Organization selection is rendered globally by Page.
 *
 * Required fileManager fields:
 *  • availableOrgs
 *  • selectedOrg
 *
 * Optional fileManager fields:
 *  • newLabel: string (e.g. "New Issue", "New Portfolio", "New Fund")
 *
 * Recents behavior:
 *  • Removed entirely (no dropdown, no recent list)
 */
export default function FileRibbon({
  entityName = "Record",
  fileManager,

  children,

  onNew,
  onSave,
  onSaveAs,
  onCopy,
  onDelete,
  onOpenBrowse,

  currentIssuer,
  currentName,
  currentOwner,
  currentStatus,
  animateSaving = false,

  isReadOnly = false,
  isLibrary = false,
}) {
  if (!fileManager) {
    console.error("FileRibbon rendered without fileManager adapter");
    return null;
  }

  const {
    availableOrgs = [],
    selectedOrg = null,
    newLabel,
  } = fileManager;

  const singleOrg = Array.isArray(availableOrgs) && availableOrgs.length === 1;
  const actionsEnabled = !!selectedOrg || singleOrg;

  const statusLabel = (currentStatus || "unsaved").toLowerCase();
  const isSaving = statusLabel === "saving";
  const showStatusText = !isReadOnly;

  const canOpen = typeof onOpenBrowse === "function";

  const resolvedNewLabel =
    typeof newLabel === "string" && newLabel.trim()
      ? newLabel.trim()
      : `New ${entityName}`;

  const showBadges = !!(isReadOnly || isLibrary);
  const ownerName = [currentOwner?.firstName, currentOwner?.lastName]
    .filter(Boolean)
    .join(" ");

  const canSave = typeof onSave === "function" && !isReadOnly && !isLibrary;
  const canSaveAs = typeof onSaveAs === "function";

  const actionCls =
    "cursor-pointer hover:text-brand font-medium flex items-center gap-1";
  const deleteCls =
    "cursor-pointer text-red-600 hover:text-red-500 font-medium flex items-center gap-1";

  return (
    <div className="flex flex-col gap-1">
      <div className="rounded-md bg-[hsl(var(--background))] px-2 py-2 text-app">
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 text-sm flex-wrap min-h-6">
              {actionsEnabled && (
                <>
                  {onNew && (
                    <span
                      onClick={onNew}
                      className={actionCls}
                      aria-label={resolvedNewLabel}
                    >
                      <PlusCircle className="h-4 w-4" />
                      <span>{resolvedNewLabel}</span>
                    </span>
                  )}

                  {canOpen && (
                    <span
                      onClick={onOpenBrowse}
                      className={actionCls}
                      aria-label={`Open ${entityName}`}
                    >
                      <FolderOpen className="h-4 w-4" />
                      <span>Open</span>
                    </span>
                  )}

                  {canSave && (
                    <span onClick={onSave} className={actionCls}>
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </span>
                  )}

                  {canSaveAs && (
                    <span onClick={onSaveAs} className={actionCls}>
                      <PlusCircle className="h-4 w-4" />
                      <span>Save As</span>
                    </span>
                  )}

                  {onCopy && (
                    <span onClick={onCopy} className={actionCls}>
                      <Copy className="h-4 w-4" />
                      <span>Copy</span>
                    </span>
                  )}

                  {onDelete && (
                    <span onClick={onDelete} className={deleteCls}>
                      <Trash2 className="h-4 w-4" />
                      <span>Delete</span>
                    </span>
                  )}
                </>
              )}
            </div>

            <div className="flex flex-col items-end justify-center gap-0.5 text-sm min-h-6">
              <div className="flex items-center justify-end gap-2 flex-wrap">
                {currentName ? (
                  <>
                    <span className="text-muted">Current:</span>

                    {currentIssuer ? (
                      <span className="font-medium text-app">
                        {currentIssuer},
                      </span>
                    ) : null}

                    <span className="font-medium text-app">{currentName}</span>

                    {showStatusText ? (
                      <span className="inline-flex min-w-[5.5rem] items-center gap-1 text-muted">
                        (
                        {animateSaving && isSaving ? (
                          <LoaderCircle
                            aria-hidden="true"
                            className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                          />
                        ) : null}
                        <span>{statusLabel}</span>)
                      </span>
                    ) : null}
                  </>
                ) : null}

              {showBadges ? (
                <>
                  {isLibrary ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted ml-2">
                      <BookOpen className="h-3.5 w-3.5" />
                      Library
                    </span>
                  ) : null}

                  {isReadOnly ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted ml-2">
                      <Lock className="h-3.5 w-3.5" />
                      Read-Only
                    </span>
                  ) : null}
                </>
              ) : null}
              </div>
              {currentName && ownerName ? (
                <div className="flex items-center justify-end gap-2 text-xs">
                  <span className="text-muted">Created by:</span>
                  <span className="font-medium text-app">{ownerName}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {children ? children : null}
    </div>
  );
}
